import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { CalculatorIcon, DownloadIcon } from "@heroicons/react/outline";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import PayrollRunItemDetail from "../../../../Components/v2/Accounting/PayrollRunItemDetail";
import PayrollPaymentModal from "../../../../Components/v2/Accounting/PayrollPaymentModal";
import PeriodClosedNotice from "../../../../Components/v2/Accounting/PeriodClosedNotice";
import TransactionDetailModal from "../../../../Components/v2/Accounting/TransactionDetailModal";
import PremiumFeatureModal, { PremiumTag } from "../../../../Components/v2/PremiumFeatureModal";
import FormDate from "../../../../Components/v2/Form/FormDate";
import Table from "../../../../Components/v2/Table";
import Button from "../../../../Components/v2/Button";
import Badge from "../../../../Components/Badge";
import { Alert } from "../../../../Components/Alert";
import { ModalAlert } from "../../../../Components/ModalAlert";
import {
  addLocalNotification,
  exportPayslips,
  getPayrollJournal,
  requestListAccount,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePayrollRun,
  usePeriodLock,
} from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import { usePayrollDownload } from "../../../../Hooks/usePayrollDownload";
import { formatMoney, getPeriodClosedError } from "../../../../Utils";
import {
  formatPeriodDate,
  formatPeriodDateTime,
  formatPeriodMonth,
} from "../../../../Utils/PeriodUtils";
import {
  fromPeriod,
  getErrorMessage,
  getMissingAccountCodes,
  getRemittanceAmounts,
  getRemittanceDueDate,
  getSkipReasonKey,
  getSkippedFromError,
  isPeriod,
  periodEnd,
  REMITTANCES,
  toPeriod,
} from "../../../../Utils/payroll";
import FormError from "../../../../Components/v2/Form/FormError";

// Pressable primary actions confirm the click the way the period-close panel
// does; the scale is dropped for anyone who asked for less motion.
const PRESS = "transition-transform duration-150 ease-out active:scale-[0.97] motion-reduce:transform-none";

// The badge says the state in words; colour only backs it up.
const STATUS_BADGE = { DRAFT: "secondary", POSTED: "info", PAID: "success" };

const Money = ({ value }) => (
  <span className="tabular-nums">{formatMoney(value)}</span>
);

const SummaryCard = ({ label, value, footer, isLoading }) => (
  <div className="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-5">
    {isLoading ? (
      <div className="animate-pulse" aria-hidden="true">
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="h-7 bg-gray-200 rounded w-3/4" />
      </div>
    ) : (
      <>
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-xl font-semibold text-gray-900 tabular-nums sm:text-2xl">
          {formatMoney(value)}
        </dd>
        {footer && <dd className="mt-1 text-sm text-gray-500">{footer}</dd>}
      </>
    )}
  </div>
);

const monthFormatter = (locale) => {
  const format = new Intl.DateTimeFormat(locale === "en" ? "en-US" : "id-ID", {
    month: "short",
  });
  return (months) =>
    months.map((month) => format.format(new Date(2000, month - 1, 1))).join(", ");
};

const AccountingPayroll = () => {
  const { t, i18n } = useTranslation();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const [searchParams, setSearchParams] = useSearchParams();

  const monthParam = searchParams.get("month");
  const period = isPeriod(monthParam) ? monthParam : toPeriod(new Date());
  const businessID = user?.mainBusiness?.id;
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const {
    run,
    items,
    loading,
    error,
    refetch,
    create,
    creating,
    discard,
    discarding,
    post,
    posting,
    pay,
    paying,
    cancelPayment,
    cancellingPayment,
    voidRun,
    voiding,
    remit,
    cancelRemittance,
    remittingKind,
    cancellingRemittanceKind,
    actionError,
    clearActionError,
  } = usePayrollRun(businessID, period);

  const accountingDispatch = useAccountingDispatch();
  const { accounts } = useAccountingState();
  const payslips = usePayrollDownload();
  const {
    isDateClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(businessID, { locale: i18n.language });

  const status = run?.status;
  const booked = status === "POSTED" || status === "PAID";
  const busy =
    creating ||
    discarding ||
    posting ||
    paying ||
    cancellingPayment ||
    voiding ||
    Boolean(remittingKind) ||
    Boolean(cancellingRemittanceKind);

  // The accrual is dated the month's last day; posting and voiding both write
  // there, so a closed month locks both. A cancelled payment is reversed on
  // the payment's own date.
  const accrualDate = periodEnd(period);
  const accrualLocked = isDateClosed(accrualDate);
  const paymentLocked = Boolean(run?.paidAt) && isDateClosed(run.paidAt);

  // The paid-from account is named from the chart; it is loaded once a run is
  // in the ledger, since only then does anything here refer to an account.
  useEffect(() => {
    if (!businessID || !booked) return;
    requestListAccount(accountingDispatch, { page: 1, businessID, limit: 200, loadAll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessID, booked]);
  const accountLabel = (accountID) => {
    const account = (accounts || []).find((candidate) => candidate.id === accountID);
    return account ? `${account.code} ${account.name}` : null;
  };

  const remittanceAmounts = useMemo(() => getRemittanceAmounts(items), [items]);
  // A paid withholding debited the payable the accrual credited; the accrual
  // cannot be voided while one stands.
  const paidRemittances = REMITTANCES.filter((r) => run?.[`${r.prefix}TransactionID`]);

  // The drawer keeps its last item while it slides out, so closing animates
  // instead of blanking mid-transition.
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const openDetail = (item) => {
    setSelectedItem(item);
    setDetailOpen(true);
    payslips.clearError();
  };

  // Payslips of a posted run, as PDF: everyone's in one file, or one employee's.
  const downloadAllPayslips = () =>
    payslips.download("all", () => exportPayslips({ runId: run.id }));
  const downloadPayslip = (item) =>
    payslips.download(item.employeeID, () =>
      exportPayslips({ runId: run.id, employeeId: item.employeeID }),
    );
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Which confirmation, "post" | "cancelPayment" | "void" |
  // "cancelRemittance" (of `confirmRemittance`). It outlives the dialog so the
  // text does not change while it fades out.
  const [confirmKind, setConfirmKind] = useState("post");
  const [confirmRemittance, setConfirmRemittance] = useState("PPH21");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const askConfirm = (kind, remittanceKind) => {
    setConfirmKind(kind);
    if (remittanceKind) setConfirmRemittance(remittanceKind);
    setConfirmOpen(true);
  };
  // What the payment dialog pays: "NET_PAY" or a withholding's kind. Kept
  // while the dialog fades out, like the confirmation.
  const [paymentKind, setPaymentKind] = useState("NET_PAY");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const openPayment = (kind) => {
    setPaymentKind(kind);
    setPaymentOpen(true);
  };

  // The journal viewer keeps its last journal while it slides out, like the
  // item drawer.
  const [journal, setJournal] = useState(null);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journalLoading, setJournalLoading] = useState(null);
  const [journalError, setJournalError] = useState(null);

  const monthLabel = formatPeriodMonth(`${period}-15`, i18n.language);
  const accrualDateLabel = formatPeriodDate(accrualDate, i18n.language);
  const formatMonths = useMemo(() => monthFormatter(i18n.language), [i18n.language]);

  // A 422 means nobody in the month could be paid; its list of skipped
  // employees says why, which is the useful part.
  const skippedFromError = getSkippedFromError(actionError);

  const handleMonthChange = (date) => {
    if (!date) return;
    setDetailOpen(false);
    payslips.clearError();
    setSearchParams({ month: toPeriod(date) });
  };

  const handleCreate = useCallback(async () => {
    const data = await create();
    if (!data) return;
    addLocalNotification(notificationDispatch, {
      id: `payroll-run-${data.run.id}-${data.run.revision}`,
      type: "success",
      title: "common.success",
      body:
        data.run.revision > 1
          ? "payroll.notifications.recomputed"
          : "payroll.notifications.computed",
      showFull: true,
    });
  }, [create, notificationDispatch]);

  const handleDiscard = async () => {
    const ok = await discard();
    setConfirmDiscard(false);
    if (!ok) return;
    setDetailOpen(false);
    addLocalNotification(notificationDispatch, {
      id: `payroll-discard-${period}`,
      type: "success",
      title: "common.success",
      body: "payroll.notifications.discarded",
      showFull: true,
    });
  };

  const notify = (id, body) =>
    addLocalNotification(notificationDispatch, {
      id,
      type: "success",
      title: "common.success",
      body,
      showFull: true,
    });

  const CONFIRMED = {
    post: { action: post, body: "payroll.notifications.posted" },
    cancelPayment: { action: cancelPayment, body: "payroll.notifications.paymentCancelled" },
    void: { action: voidRun, body: "payroll.notifications.voided" },
    cancelRemittance: {
      action: () => cancelRemittance(confirmRemittance),
      body: "payroll.remittances.notifications.cancelled",
    },
  };

  const handleConfirmed = async () => {
    const { action, body } = CONFIRMED[confirmKind];
    const runID = run?.id;
    const data = await action();
    setConfirmOpen(false);
    if (!data) return;
    if (data.run.status === "VOID") setDetailOpen(false);
    notify(`payroll-${confirmKind}-${runID}-${data.run.updatedAt}`, body);
  };

  const handlePayment = async (payment) => {
    const netPay = paymentKind === "NET_PAY";
    const data = netPay ? await pay(payment) : await remit(paymentKind, payment);
    if (!data) return;
    setPaymentOpen(false);
    notify(
      `payroll-pay-${paymentKind}-${data.run.id}-${data.run.updatedAt}`,
      netPay ? "payroll.notifications.paid" : `payroll.remittances.notifications.${paymentKind}`,
    );
  };

  const closePayment = () => {
    setPaymentOpen(false);
    // Its error belongs to the dialog; it should not linger on the page.
    clearActionError();
  };

  const openJournal = async (transactionID) => {
    setJournalLoading(transactionID);
    setJournalError(null);
    try {
      const found = await getPayrollJournal({ id: transactionID });
      if (!found) throw new Error("Journal not found");
      setJournal(found);
      setJournalOpen(true);
    } catch (err) {
      setJournalError(transactionID);
    } finally {
      setJournalLoading(null);
    }
  };

  // Dialog errors stay in the dialog.
  const pageError = paymentOpen ? null : actionError;
  const periodClosed = Boolean(getPeriodClosedError(pageError));
  const missingAccountCodes = getMissingAccountCodes(pageError);

  const projectedItems = items.filter(
    (item) => item.isLastTaxPeriod && item.ytdProjectedMonths?.length,
  );
  const skipped = run?.skipped || [];

  const columns = [
    {
      header: "payroll.table.employee",
      accessor: (item) => (
        <button
          type="button"
          onClick={() => openDetail(item)}
          aria-label={t("payroll.actions.viewDetail", { name: item.employeeName })}
          className="group text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <span className="block font-medium text-blue-600 group-hover:text-blue-700 group-hover:underline">
            {item.employeeName}
          </span>
          <span className="block text-xs text-gray-500">
            {[item.employeeCode, item.ptkpStatus, `TER ${item.terCategory}`]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </button>
      ),
    },
    {
      header: "payroll.table.gross",
      align: "right",
      className: "tabular-nums text-gray-900",
      accessor: (item) => formatMoney(item.grossSalary),
    },
    {
      header: "payroll.table.pph21",
      align: "right",
      className: "tabular-nums text-gray-900",
      accessor: (item) => formatMoney(item.pph21),
    },
    {
      header: "payroll.table.deductions",
      align: "right",
      className: "tabular-nums text-gray-900",
      accessor: (item) => formatMoney(item.employeeDeductions),
    },
    {
      header: "payroll.table.netPay",
      align: "right",
      className: "tabular-nums font-medium text-gray-900",
      accessor: (item) => formatMoney(item.netPay),
    },
  ];

  const downloadAllButton = (
    <Button
      variant="secondary"
      leadingIcon={DownloadIcon}
      onClick={downloadAllPayslips}
      loading={payslips.downloading === "all"}
      disabled={busy || Boolean(payslips.downloading)}
    >
      {t("payroll.payslip.downloadAll")}
      {payslips.isFree && <PremiumTag />}
    </Button>
  );

  // One primary per state, last in the row (first when stacked on a phone).
  const headerActions =
    status === "DRAFT" ? (
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          variant="secondary"
          color="red"
          onClick={() => setConfirmDiscard(true)}
          disabled={writeBlocked || busy}
        >
          {t("payroll.actions.discard")}
        </Button>
        <Button
          variant="secondary"
          onClick={handleCreate}
          loading={creating}
          disabled={writeBlocked || busy}
        >
          {t("payroll.actions.recompute")}
        </Button>
        <Button
          onClick={() => askConfirm("post")}
          loading={posting}
          disabled={writeBlocked || busy || accrualLocked}
          className={PRESS}
        >
          {t("payroll.actions.post")}
        </Button>
      </div>
    ) : status === "POSTED" ? (
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          variant="secondary"
          color="red"
          onClick={() => askConfirm("void")}
          loading={voiding}
          disabled={writeBlocked || busy || accrualLocked || paidRemittances.length > 0}
        >
          {t("payroll.actions.void")}
        </Button>
        {downloadAllButton}
        <Button
          onClick={() => openPayment("NET_PAY")}
          disabled={writeBlocked || busy}
          className={PRESS}
        >
          {t("payroll.actions.pay")}
        </Button>
      </div>
    ) : status === "PAID" ? (
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          variant="secondary"
          onClick={() => askConfirm("cancelPayment")}
          loading={cancellingPayment}
          disabled={writeBlocked || busy || paymentLocked}
        >
          {t("payroll.actions.cancelPayment")}
        </Button>
        {downloadAllButton}
      </div>
    ) : null;

  const statusNote =
    status === "POSTED"
      ? t("payroll.status.postedNote", { date: accrualDateLabel })
      : status === "PAID"
        ? accountLabel(run.paymentAccountID)
          ? t("payroll.status.paidNote", {
              date: formatPeriodDate(run.paidAt, i18n.language),
              account: accountLabel(run.paymentAccountID),
            })
          : t("payroll.status.paidOn", { date: formatPeriodDate(run.paidAt, i18n.language) })
        : t("payroll.status.draftNote");
  const lockedByPeriod = status === "PAID" ? paymentLocked : accrualLocked;

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader title="payroll.title" additionalActions={headerActions} />

        <div className="px-4 py-5 sm:px-6 lg:px-8 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full sm:max-w-xs">
              <FormDate
                label="payroll.filter.month"
                selected={fromPeriod(period)}
                onChange={handleMonthChange}
                showMonthYearPicker
                dateFormat="MMM yyyy"
              />
            </div>

            {run && (
              <div className="text-sm text-gray-600 sm:text-right">
                <div className="flex items-center gap-2 sm:justify-end">
                  <Badge type={STATUS_BADGE[status] || "secondary"} title={`payroll.status.${status}`} />
                  <span>
                    {t("payroll.status.computedAt", {
                      date: formatPeriodDateTime(run.computedAt, i18n.language),
                    })}
                  </span>
                </div>
                <p className="mt-1 text-gray-500">{statusNote}</p>
                {status === "PAID" && !paymentLocked && (
                  <p className="mt-1 text-gray-500">{t("payroll.status.voidAfterCancel")}</p>
                )}
                {status === "POSTED" && paidRemittances.length > 0 && (
                  <p className="mt-1 text-gray-500">{t("payroll.remittances.voidAfterCancel")}</p>
                )}
                {lockedByPeriod && (
                  <p className="mt-1 text-xs text-amber-700">
                    {t("accounting.period.hints.dateLocked", {
                      date: closedThroughLabel,
                    })}
                  </p>
                )}
              </div>
            )}
          </div>

          <PeriodClosedNotice error={pageError} onRefresh={refetchPeriod} />

          {missingAccountCodes && (
            <Alert
              type="warning"
              title={getErrorMessage(pageError)}
              renderActions={() => (
                <Link
                  to="/v2/accounting/coa"
                  className="mt-2 inline-block text-sm font-medium text-yellow-800 underline hover:text-yellow-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-600 rounded"
                >
                  {t("payroll.missingAccounts.manage")}
                </Link>
              )}
            />
          )}

          {pageError && !skippedFromError && !periodClosed && !missingAccountCodes && (
            <FormError error={pageError} />
          )}

          {!detailOpen && <FormError error={payslips.error} />}

          {skippedFromError && (
            <Alert
              type="warning"
              title={getErrorMessage(actionError) || "payroll.noEmployees"}
              renderActions={() => (
                <SkippedList skipped={skippedFromError} t={t} className="text-yellow-700" />
              )}
            />
          )}

          {error ? (
            <Table columns={columns} error={error} onRetry={refetch} />
          ) : loading || run ? (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard
                  isLoading={loading}
                  label={t("payroll.summary.gross")}
                  value={run?.totalGross}
                  footer={t("payroll.summary.employeeCount", { count: run?.employeeCount })}
                />
                <SummaryCard
                  isLoading={loading}
                  label={t("payroll.summary.pph21")}
                  value={run?.totalPph21}
                  footer={
                    run?.totalTaxAllowance
                      ? t("payroll.summary.taxAllowance", {
                          amount: formatMoney(run.totalTaxAllowance),
                        })
                      : null
                  }
                />
                <SummaryCard
                  isLoading={loading}
                  label={t("payroll.summary.netPay")}
                  value={run?.totalNetPay}
                  footer={t("payroll.summary.employeeDeductions", {
                    amount: formatMoney(run?.totalEmployeeDeductions),
                  })}
                />
                <SummaryCard
                  isLoading={loading}
                  label={t("payroll.summary.employerContributions")}
                  value={run?.totalEmployerContributions}
                  footer={t("payroll.summary.employerContributionsNote")}
                />
              </dl>

              {!loading && booked && (
                <section
                  aria-labelledby="payroll-journals-title"
                  className="bg-white shadow rounded-lg overflow-hidden"
                >
                  <h2
                    id="payroll-journals-title"
                    className="px-4 py-3 sm:px-5 text-sm font-semibold text-gray-900 border-b border-gray-100"
                  >
                    {t("payroll.journals.title")}
                  </h2>
                  <ul className="divide-y divide-gray-100">
                    <JournalRow
                      t={t}
                      name={t("payroll.journals.accrual")}
                      date={accrualDateLabel}
                      transactionID={run.accrualTransactionID}
                      loading={journalLoading === run.accrualTransactionID}
                      failed={journalError === run.accrualTransactionID}
                      onView={openJournal}
                    />
                    {status === "PAID" && (
                      <JournalRow
                        t={t}
                        name={t("payroll.journals.payment")}
                        date={formatPeriodDate(run.paidAt, i18n.language)}
                        amount={run.totalNetPay}
                        transactionID={run.paymentTransactionID}
                        loading={journalLoading === run.paymentTransactionID}
                        failed={journalError === run.paymentTransactionID}
                        onView={openJournal}
                      />
                    )}
                    {paidRemittances.map((remittance) => {
                      const transactionID = run[`${remittance.prefix}TransactionID`];
                      return (
                        <JournalRow
                          key={remittance.kind}
                          t={t}
                          name={t(`payroll.remittances.kinds.${remittance.kind}`)}
                          date={formatPeriodDate(run[`${remittance.prefix}PaidAt`], i18n.language)}
                          amount={remittanceAmounts[remittance.kind]}
                          transactionID={transactionID}
                          loading={journalLoading === transactionID}
                          failed={journalError === transactionID}
                          onView={openJournal}
                        />
                      );
                    })}
                  </ul>
                </section>
              )}

              {!loading && booked && (
                <section
                  aria-labelledby="payroll-remittances-title"
                  className="bg-white shadow rounded-lg overflow-hidden"
                >
                  <h2
                    id="payroll-remittances-title"
                    className="px-4 py-3 sm:px-5 text-sm font-semibold text-gray-900 border-b border-gray-100"
                  >
                    {t("payroll.remittances.title")}
                  </h2>
                  <ul className="divide-y divide-gray-100">
                    {REMITTANCES.map((remittance) => {
                      const paidAt = run[`${remittance.prefix}PaidAt`];
                      return (
                        <RemittanceRow
                          key={remittance.kind}
                          t={t}
                          locale={i18n.language}
                          remittance={remittance}
                          amount={remittanceAmounts[remittance.kind]}
                          dueDate={getRemittanceDueDate(remittance, period)}
                          paidAt={paidAt}
                          account={accountLabel(run[`${remittance.prefix}AccountID`])}
                          ntpn={remittance.kind === "PPH21" ? run.pph21Ntpn : null}
                          paying={remittingKind === remittance.kind}
                          cancelling={cancellingRemittanceKind === remittance.kind}
                          disabled={writeBlocked || busy}
                          cancelLocked={Boolean(paidAt) && isDateClosed(paidAt)}
                          onPay={() => openPayment(remittance.kind)}
                          onCancel={() => askConfirm("cancelRemittance", remittance.kind)}
                        />
                      );
                    })}
                  </ul>
                </section>
              )}

              {!loading && skipped.length > 0 && (
                <Alert
                  type="info"
                  title="payroll.skipped.title"
                  renderActions={() => (
                    <SkippedList skipped={skipped} t={t} className="text-blue-700" />
                  )}
                />
              )}

              {!loading && projectedItems.length > 0 && (
                <Alert
                  type="warning"
                  title="payroll.projected.title"
                  renderActions={() => (
                    <ul className="mt-1 list-disc pl-5 space-y-1 text-sm text-yellow-700">
                      {projectedItems.map((item) => (
                        <li key={item.id}>
                          {t("payroll.projected.item", {
                            name: item.employeeName,
                            months: formatMonths(item.ytdProjectedMonths),
                          })}
                        </li>
                      ))}
                    </ul>
                  )}
                />
              )}

              <Table
                columns={columns}
                data={items}
                isLoading={loading}
                footer={
                  run
                    ? [
                        t("payroll.table.total"),
                        <Money value={run.totalGross} />,
                        <Money value={run.totalPph21} />,
                        <Money value={run.totalEmployeeDeductions} />,
                        <Money value={run.totalNetPay} />,
                      ]
                    : null
                }
              />
            </>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white px-6 py-10 text-center">
              <CalculatorIcon className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
              <h3 className="mt-3 text-sm font-semibold text-gray-900">
                {t("payroll.empty.title", { month: monthLabel })}
              </h3>
              <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                {t("payroll.empty.body")}
              </p>
              <Button
                className={`mt-5 ${PRESS}`}
                onClick={handleCreate}
                loading={creating}
                disabled={writeBlocked || !businessID}
              >
                {t("payroll.actions.compute")}
              </Button>
            </div>
          )}
        </div>

        <PayrollRunItemDetail
          open={detailOpen && Boolean(selectedItem)}
          onClose={() => setDetailOpen(false)}
          item={selectedItem}
          monthLabel={monthLabel}
          formatMonths={formatMonths}
          onDownload={booked ? downloadPayslip : undefined}
          downloading={Boolean(selectedItem) && payslips.downloading === selectedItem.employeeID}
          downloadError={detailOpen ? payslips.error : null}
          isFree={payslips.isFree}
        />

        <PremiumFeatureModal
          open={payslips.premiumOpen}
          setOpen={payslips.setPremiumOpen}
          description={t("payroll.payslip.premium")}
        />

        <ModalAlert
          open={confirmDiscard}
          setOpen={setConfirmDiscard}
          title="payroll.discard.title"
          descriptionNode={t("payroll.discard.body", {
            month: monthLabel,
            count: run?.employeeCount || 0,
          })}
          confirmButtonText="payroll.discard.confirm"
          cancelButtonText="common.cancel"
          loadingButton={discarding}
          disabledButton={discarding}
          onConfirm={handleDiscard}
          onCancel={() => setConfirmDiscard(false)}
        />

        <ModalAlert
          open={confirmOpen}
          setOpen={setConfirmOpen}
          type={
            {
              post: "primary",
              cancelPayment: "warning",
              void: "danger",
              cancelRemittance: "warning",
            }[confirmKind]
          }
          title={
            confirmKind === "cancelRemittance"
              ? t("payroll.remittances.cancel.title", {
                  name: t(`payroll.remittances.kinds.${confirmRemittance}`),
                  month: monthLabel,
                })
              : t(`payroll.${confirmKind}.title`, { month: monthLabel })
          }
          descriptionNode={
            confirmKind === "cancelRemittance"
              ? t("payroll.remittances.cancel.body", {
                  amount: formatMoney(remittanceAmounts[confirmRemittance]),
                  date: formatPeriodDate(
                    run?.[
                      `${REMITTANCES.find((r) => r.kind === confirmRemittance).prefix}PaidAt`
                    ],
                    i18n.language,
                  ),
                })
              : confirmKind === "cancelPayment"
              ? t("payroll.cancelPayment.body", {
                  amount: formatMoney(run?.totalNetPay),
                  date: formatPeriodDate(run?.paidAt, i18n.language),
                })
              : confirmKind === "void"
                ? t("payroll.void.body", { date: accrualDateLabel })
                : t("payroll.post.body", {
                    date: accrualDateLabel,
                    netPay: formatMoney(run?.totalNetPay),
                    pph21: formatMoney(run?.totalPph21),
                  })
          }
          confirmButtonText={
            confirmKind === "cancelRemittance"
              ? "payroll.remittances.cancel.confirm"
              : `payroll.${confirmKind}.confirm`
          }
          // "Batalkan" beside "Batalkan pembayaran" reads as the same choice.
          cancelButtonText={
            ["cancelPayment", "cancelRemittance"].includes(confirmKind)
              ? "common.back"
              : "common.cancel"
          }
          loadingButton={
            posting || cancellingPayment || voiding || Boolean(cancellingRemittanceKind)
          }
          disabledButton={busy}
          onConfirm={handleConfirmed}
          onCancel={() => setConfirmOpen(false)}
        />

        <PayrollPaymentModal
          open={paymentOpen}
          onClose={closePayment}
          run={run}
          businessID={businessID}
          {...(paymentKind === "NET_PAY"
            ? {
                title: t("payroll.pay.title", { month: monthLabel }),
                amount: run?.totalNetPay,
                amountLabel: t("payroll.pay.amount"),
                amountNote: t("payroll.summary.employeeCount", { count: run?.employeeCount }),
                submitLabel: t("payroll.pay.submit", { amount: formatMoney(run?.totalNetPay) }),
                note: t("payroll.pay.journalNote"),
              }
            : {
                title: t(`payroll.remittances.dialog.title.${paymentKind}`, { month: monthLabel }),
                amount: remittanceAmounts[paymentKind],
                amountLabel: t(`payroll.remittances.dialog.amount.${paymentKind}`),
                amountNote: t(`payroll.remittances.dialog.amountNote.${paymentKind}`),
                submitLabel: t(`payroll.remittances.dialog.submit.${paymentKind}`, {
                  amount: formatMoney(remittanceAmounts[paymentKind]),
                }),
                note: t(`payroll.remittances.dialog.note.${paymentKind}`),
                withNtpn: paymentKind === "PPH21",
              })}
          onSubmit={handlePayment}
          submitting={paying || Boolean(remittingKind)}
          error={paymentOpen ? actionError : null}
        />

        <TransactionDetailModal
          open={journalOpen}
          onClose={() => setJournalOpen(false)}
          transaction={journal}
        />
      </main>
    </AccountingWrapper>
  );
};

const JournalRow = ({ t, name, date, amount, transactionID, loading, failed, onView }) => (
  <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 sm:px-5 text-sm">
    <span className="font-medium text-gray-900">{name}</span>
    <span className="text-gray-500">{date}</span>
    {amount !== undefined && (
      <span className="tabular-nums text-gray-900">{formatMoney(amount)}</span>
    )}
    <span className="ml-auto flex items-center gap-3">
      {failed && (
        <span className="text-red-600" role="alert">
          {t("payroll.journals.error")}
        </span>
      )}
      {transactionID && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onView(transactionID)}
          loading={loading}
          aria-label={t("payroll.journals.viewFor", { name })}
        >
          {t("payroll.journals.view")}
        </Button>
      )}
    </span>
  </li>
);

// The day-level "is it late": a withholding due on the 15th is not late on
// the 15th itself.
const isOverdue = (dueDate) => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate()) > dueDate;
};

const RemittanceRow = ({
  t,
  locale,
  remittance,
  amount,
  dueDate,
  paidAt,
  account,
  ntpn,
  paying,
  cancelling,
  disabled,
  cancelLocked,
  onPay,
  onCancel,
}) => {
  const name = t(`payroll.remittances.kinds.${remittance.kind}`);
  const owed = amount > 0;
  const status = paidAt
    ? [
        account
          ? t("payroll.remittances.paidNote", { date: formatPeriodDate(paidAt, locale), account })
          : t("payroll.remittances.paidOn", { date: formatPeriodDate(paidAt, locale) }),
        ntpn ? t("payroll.remittances.ntpn", { ntpn }) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : owed
      ? t("payroll.remittances.due", { date: formatPeriodDate(dueDate, locale) })
      : t("payroll.remittances.nothingOwed");

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-5 text-sm">
      <div className="min-w-0 flex-1 basis-48">
        <p className="font-medium text-gray-900">{name}</p>
        <p className="text-gray-500">
          {status}
          {!paidAt && owed && isOverdue(dueDate) && (
            <span className="ml-2 font-medium text-amber-700">
              {t("payroll.remittances.overdue")}
            </span>
          )}
        </p>
      </div>
      {(owed || paidAt) && (
        <span className="tabular-nums text-gray-900">{formatMoney(amount)}</span>
      )}
      <span className="flex items-center gap-2">
        {paidAt ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            loading={cancelling}
            disabled={disabled || cancelLocked}
            aria-label={t("payroll.remittances.actions.cancelFor", { name })}
          >
            {t("payroll.remittances.actions.cancel")}
          </Button>
        ) : (
          // No e-billing link: the e-billing form only covers tax years up to 2024,
          // and later years are billed in Coretax.
          owed && (
            <Button
              size="sm"
              onClick={onPay}
              loading={paying}
              disabled={disabled}
              // The visible verb leads the name: "Setor PPh 21", "Bayar BPJS ...".
              aria-label={`${t(`payroll.remittances.actions.pay.${remittance.kind}`)} ${name}`}
            >
              {t(`payroll.remittances.actions.pay.${remittance.kind}`)}
            </Button>
          )
        )}
      </span>
    </li>
  );
};

const SkippedList = ({ skipped, t, className }) => (
  <ul className={`mt-1 space-y-1 text-sm ${className}`}>
    {skipped.map((skip) => (
      <li key={skip.employeeID}>
        <span className="font-medium">{skip.employeeName || skip.employeeID}</span>
        {" — "}
        {t(getSkipReasonKey(skip.reason))}
      </li>
    ))}
  </ul>
);

export default AccountingPayroll;
